import { useEffect, useState, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { fetchWithAuth } from '../utils/fetchWithAuth'
import Overlay from './Overlay'
import MessageBox from './MessageBox'
import arrowLeft from '../assets/images/icons/arrow_left.png'
import arrowRight from '../assets/images/icons/arrow_right.png'
import {
  modalPrimaryButtonStyle,
  modalSecondaryButtonStyle
} from '../styles/buttonStyles'

const fields = [
  ['date', 'TRIPLOG.SERVICECOSTS.TABLE.date'],
  ['vehicleId', 'TRIPLOG.SERVICECOSTS.TABLE.vehicleId'],
  ['spz', 'vehicle_license_plate'],
  ['serviceType', 'TRIPLOG.SERVICECOSTS.TABLE.serviceType'],
  ['kilometers', 'TRIPLOG.SERVICECOSTS.TABLE.kilometers'],
  ['laborCost', 'TRIPLOG.SERVICECOSTS.TABLE.laborCost'],
  ['materialCost', 'TRIPLOG.SERVICECOSTS.TABLE.materialCost'],
  ['totalCost', 'TRIPLOG.SERVICECOSTS.TABLE.totalCost'],
  ['description', 'TRIPLOG.SERVICECOSTS.TABLE.description'],
  ['workshopName', 'TRIPLOG.SERVICECOSTS.TABLE.workshopName'],
  ['workshopAddress', 'TRIPLOG.SERVICECOSTS.TABLE.workshopAddress'],
  ['documentNumber', 'TRIPLOG.SERVICECOSTS.TABLE.documentNumber'],
  ['note', 'TRIPLOG.SERVICECOSTS.TABLE.note'],
]

const NUMERIC_FIELDS = [
  'kilometers',
  'laborCost',
  'materialCost',
  'totalCost'
]

const Row = ({ htmlFor, label, children, layout }) => (
  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
    <label
      htmlFor={htmlFor}
      style={{
        width: layout.labelW,
        fontWeight: 700,
        fontSize: layout.labelFont
      }}
    >
      {label}
    </label>

    <div style={{ display: 'flex', alignItems: 'center', width: layout.inputW }}>
      {children}
    </div>
  </div>
)

const ServiceDetailModal = ({
  open,
  onClose,
  service,
  onSaved,
  onPrevService,
  onNextService,
  hasPrevService = false,
  hasNextService = false
}) => {
  const modalRef = useRef(null)
  const { t } = useTranslation()

  const [vw, setVw] = useState(window.innerWidth)
let layout = {}

if (vw >= 1920) {
  layout = {
    labelW: 250,
    inputW: 350,
    inputH: 20,
    labelFont: 15,
    inputFont: 15,
    rowGap: 4
  }
}
else if (vw >= 1366) {
  layout = {
    labelW: 220,
    inputW: 340,
    inputH: 18,
    labelFont: 14,
    inputFont: 14,
    rowGap: 3
  }
}
else {
  layout = {
    labelW: 200,
    inputW: 240,
    inputH: 18,
    labelFont: 13,
    inputFont: 13,
    rowGap: 3
  }
}

const inputStyle = {
  height: layout.inputH,
  fontSize: layout.inputFont,
  padding: '6px 10px',
  borderRadius: 6,
  border: '1px solid #999',
  width: '100%'
}

  const [form, setForm] = useState({})
  const [original, setOriginal] = useState({})
  const [vehiclesById, setVehiclesById] = useState({})
  const [errors, setErrors] = useState({})
  const [loading, setLoading] = useState(false)
  const [message, setMessage] = useState(null)
  const [editMode, setEditMode] = useState(false)
  const [pressedBtn, setPressedBtn] = useState(null)
  const [hoverBtn, setHoverBtn] = useState(null)
  const showServiceNavigation = Boolean(onPrevService || onNextService)

  useEffect(() => {
    const handleResize = () => setVw(window.innerWidth)
    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [])

  useEffect(() => {
    if (!open) return

    const trapModalFocus = (e) => {
      if (e.key !== 'Tab') return false

      const modalEl = modalRef.current
      if (!modalEl) return false

      const focusable = Array.from(
        modalEl.querySelectorAll(
          'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
        )
      ).filter((el) => !el.disabled && el.getAttribute('aria-hidden') !== 'true')

      if (!focusable.length) {
        e.preventDefault()
        modalEl.focus()
        return true
      }

      const first = focusable[0]
      const last = focusable[focusable.length - 1]
      const active = document.activeElement
      const isInsideModal = modalEl.contains(active)

      if (e.shiftKey) {
        if (!isInsideModal || active === first) {
          e.preventDefault()
          last.focus()
          return true
        }
      } else if (!isInsideModal || active === last) {
        e.preventDefault()
        first.focus()
        return true
      }

      return false
    }

    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        if (editMode) {
          setEditMode(false)
        } else {
          onClose()
        }
        return
      }

      if (trapModalFocus(e)) return

      if (e.key === 'ArrowLeft' && hasPrevService) {
        if (editMode) return
        e.preventDefault()
        onPrevService?.()
        return
      }

      if (e.key === 'ArrowRight' && hasNextService) {
        if (editMode) return
        e.preventDefault()
        onNextService?.()
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [open, editMode, onClose, hasPrevService, hasNextService, onPrevService, onNextService])

  useEffect(() => {
    if (!open) return

    const modalEl = modalRef.current
    if (!modalEl) return

    const focusable = modalEl.querySelector(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    )

    if (focusable && typeof focusable.focus === 'function') {
      focusable.focus()
      return
    }

    modalEl.focus()
  }, [open, editMode])

  useEffect(() => {
    if (!open || !service) return
    setForm(service)
    setOriginal(service)
    setEditMode(false)
    setMessage(null)
    setErrors({})
  }, [open, service])

  useEffect(() => {
    if (!open || !service?.userId) return

    const loadVehicles = async () => {
      try {
        const res = await fetchWithAuth(`/users/${service.userId}/vehicles`)
        if (!res.ok) return

        const data = await res.json()
        if (!Array.isArray(data)) return

        const index = data.reduce((acc, vehicle) => {
          if (vehicle?.id == null) return acc
          acc[String(vehicle.id)] = vehicle
          return acc
        }, {})

        setVehiclesById(index)
      } catch {}
    }

    loadVehicles()
  }, [open, service])

  const getVehicleLabel = () => {
    const vehicleFromList = vehiclesById[String(form?.vehicleId)]
    const vehicle = vehicleFromList || service?.vehicle

    return (
      vehicle?.name ||
      vehicle?.model ||
      vehicle?.licensePlate ||
      vehicle?.spz ||
      form?.vehicleId ||
      '-'
    )
  }

const getVehicleSpz = () => {
  const vehicleFromList = vehiclesById[String(form?.vehicleId)]
  const vehicle = vehicleFromList || service?.vehicle

  return vehicle?.licensePlate || vehicle?.spz || '-'
}

  const validate = () => {
    const newErrors = {}

    if (!form.totalCost || String(form.totalCost).trim() === '') {
      newErrors.totalCost = true
    }

    setErrors(newErrors)

    if (Object.keys(newErrors).length > 0) {
      setMessage({ message: t('TRIPLOG.FORM.REQUIRED_FIELDS'), type: 'error' })
      return false
    }

    return true
  }

  const saveService = async () => {
    if (!service?.id) return
    if (!validate()) return

    try {
      setLoading(true)

      const res = await fetchWithAuth(`/triplog/service-costs/${service.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
  ...form,
  kilometers: form.kilometers !== '' && form.kilometers != null
    ? Number(parseFloat(form.kilometers).toFixed(2))
    : null,
  laborCost: form.laborCost !== '' && form.laborCost != null
    ? Number(parseFloat(form.laborCost).toFixed(2))
    : null,
  materialCost: form.materialCost !== '' && form.materialCost != null
    ? Number(parseFloat(form.materialCost).toFixed(2))
    : null,
  totalCost: form.totalCost !== '' && form.totalCost != null
    ? Number(parseFloat(form.totalCost).toFixed(2))
    : null
})
      })

      if (!res.ok) {
        setMessage({ message: t('vehicle_save_failed'), type: 'error' })
        return
      }

      const updated = await res.json().catch(() => form)

      setOriginal(updated)
      setForm(updated)
      setEditMode(false)
//setMessage({ message: t('SAVED'), type: 'success' })
      onSaved?.(updated)

    } catch {
      setMessage({ message: t('CONNECTION_ERROR'), type: 'error' })
    } finally {
      setLoading(false)
    }
  }

  if (!open || !service) return null

  const modalMaxWidth =
    vw >= 2600 ? '18vw' :  vw >= 2400 ? '23vw' : vw >= 1920 ? '29vw' : vw >= 1366 ? '35vw' : '35vw'

  const modalMaxHeight =
    vw >= 1920 ? '85vh' : '95vh'

  const modalTitleFontSize =
    vw >= 1920 ? 24 : vw >= 1366 ? 24 : 18

const formatForDateInput = (value) => {
  if (!value) return ''
  const d = new Date(value)
  if (isNaN(d.getTime())) return ''
  return d.toISOString().split('T')[0]
}

const formatToTwoDecimals = (value) => {
  if (value === null || value === undefined || value === '') return ''
  const num = Number(value)
  return isNaN(num) ? '' : num.toFixed(2)
}

  return (
    <>
      <Overlay open={loading && false} zIndex={4000} backdropOpacity={0.75} />

      <div
        onClick={onClose}
        style={{
          position: 'fixed',
          inset: 0,
          background: 'rgba(0,0,0,0.4)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 3100,
          padding: 20
        }}
      >
        <div
          ref={modalRef}
          tabIndex={-1}
          onClick={(e) => e.stopPropagation()}
          style={{
            background: '#ededed',
            padding: '5px 30px 10px 30px',
            border: '2px solid #107c6b',
            borderRadius: 14,
            boxShadow: '0 12px 28px rgba(0,0,0,0.65)',
            width: '100%',
            maxWidth: modalMaxWidth,
            maxHeight: modalMaxHeight,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden'
          }}
        >
          <h3
            style={{
              fontSize: modalTitleFontSize,
              marginTop: 5,
              marginBottom: 20
            }}
          >
            {t('TRIPLOG.SERVICECOSTS.DETAILS')}
          </h3>

          {message && (
            <MessageBox message={message.message} type={message.type} />
          )}

          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: layout.rowGap,
              overflowY: 'auto',
              flex: 1
            }}
          >
            {fields.map(([key, labelKey]) => {

              if (key === 'vehicleId') {
                return (
                  <Row key={key} htmlFor={key} label={t(labelKey)} layout={layout}>
                    <input
                      value={getVehicleLabel()}
                      disabled
                      style={{
                        ...inputStyle,
                        background: '#ddd'
                      }}
                    />
                  </Row>
                )
              }
		if (key === 'spz') {
 		 return (
 		   <Row key={key} htmlFor={key} label={t(labelKey)} layout={layout}>
  		    <input
   		     value={getVehicleSpz()}
    		    disabled
    		    style={{
     		     ...inputStyle,
     		     background: '#ddd'
      		  }}
     		 />
    		</Row>
 		 )
		}
              const isDateField = key === 'date'
              const isTextarea = key === 'note' || key === 'description'

const displayValue =
  form[key] !== undefined && form[key] !== null && form[key] !== ''
    ? isDateField
      ? new Date(form[key]).toLocaleDateString('sk-SK')
      : NUMERIC_FIELDS.includes(key)
        ? formatToTwoDecimals(form[key])
        : form[key]
    : ''

              return (
                <Row key={key} htmlFor={key} label={t(labelKey)} layout={layout}>
                  {isTextarea ? (
                    <textarea
                      id={key}
                      value={form[key] || ''}
                      disabled={!editMode}
                      onChange={e => {
                        setForm(prev => ({ ...prev, [key]: e.target.value }))
                        setErrors(prev => ({ ...prev, [key]: false }))
                      }}
                      style={{
                        ...inputStyle,
                        resize: 'none',
                        fontFamily: 'Arial, sans-serif',
                        minHeight: layout.inputH,
                        background: !editMode ? '#ddd' : (errors[key] ? 'rgba(255,0,0,0.1)' : '#fff'),
                        border: errors[key] ? '1px solid #c62828' : inputStyle.border
                      }}
                    />
                  ) : (
<input
  id={key}
  type={isDateField && editMode ? 'date' : 'text'}
  value={
    editMode
      ? (isDateField
          ? formatForDateInput(form[key])
          : (form[key] || '')
        )
      : displayValue
  }
disabled={
  !editMode ||
  (key === 'totalCost' && (form.laborCost || form.materialCost))
}
onChange={e => {
  let val = e.target.value

  if (NUMERIC_FIELDS.includes(key)) {
    val = val.replace(',', '.')
    if (!/^\d*\.?\d{0,2}$/.test(val)) {
      return
    }
  }

setForm(prev => {
  const next = {
    ...prev,
    [key]: val
  }

  if (key === 'laborCost' || key === 'materialCost') {
    const laborRaw =
      key === 'laborCost' ? val : next.laborCost

    const materialRaw =
      key === 'materialCost' ? val : next.materialCost

    const labor = parseFloat(laborRaw)
    const material = parseFloat(materialRaw)

    const hasLabor =
      laborRaw !== undefined &&
      laborRaw !== null &&
      laborRaw !== '' &&
      !isNaN(labor)

    const hasMaterial =
      materialRaw !== undefined &&
      materialRaw !== null &&
      materialRaw !== '' &&
      !isNaN(material)

    if (hasLabor || hasMaterial) {
      next.totalCost = (
        (hasLabor ? labor : 0) +
        (hasMaterial ? material : 0)
      ).toFixed(2)
    }
  }

  return next
})

  setErrors(prev => ({
    ...prev,
    [key]: false
  }))
}}
onBlur={() => {
  if (NUMERIC_FIELDS.includes(key)) {
    setForm(prev => ({
      ...prev,
      [key]:
        prev[key] !== '' && prev[key] != null
          ? Number(prev[key]).toFixed(2)
          : ''
    }))
  }
}}
  onClick={e => {
    if (isDateField && editMode && e.target.showPicker) {
      e.target.showPicker()
    }
  }}
  style={{
    ...inputStyle,
    background: !editMode
      ? '#ddd'
      : (key === 'totalCost' && (form.laborCost || form.materialCost))
        ? '#ddd'
        : errors[key]
          ? 'rgba(255,0,0,0.1)'
          : '#fff',
    border: errors[key] ? '1px solid #c62828' : inputStyle.border,
    cursor: isDateField && editMode ? 'pointer' : 'default'
  }}
/>
                  )}
                </Row>
              )
            })}
          </div>

          <div
            style={{
              display: 'flex',
              gap: 12,
              marginTop: 15,
              justifyContent: 'center'
            }}
          >
            {!editMode ? (
              <>
                {showServiceNavigation && (
<button
  style={{
    ...modalSecondaryButtonStyle(
      pressedBtn,
      'prevService',
      hoverBtn === 'prevService'
    ),
    width: 50,
    minWidth: 40,
    padding: 0,
    opacity: !hasPrevService ? 0.4 : 1,
    cursor: !hasPrevService ? 'not-allowed' : 'pointer'
  }}
  disabled={!hasPrevService}
  onMouseEnter={() => setHoverBtn('prevService')}
  onMouseLeave={() => {
    setHoverBtn(null)
    setPressedBtn(null)
  }}
  onMouseDown={() => setPressedBtn('prevService')}
  onMouseUp={() => setPressedBtn(null)}
  onClick={() => onPrevService?.()}
>
  <img
    src={arrowLeft}
    alt="prev"
    style={{
      width: 18,
      height: 18,
      pointerEvents: 'none',
      filter: 'invert(33%) sepia(24%) saturate(746%) hue-rotate(120deg) brightness(85%) contrast(90%)'
    }}
  />
</button>
                )}
                <button
                  style={modalPrimaryButtonStyle(
                    pressedBtn,
                    'editService',
                    hoverBtn === 'editService'
                  )}
                  onMouseEnter={() => setHoverBtn('editService')}
                  onMouseLeave={() => {
                    setHoverBtn(null)
                    setPressedBtn(null)
                  }}
                  onMouseDown={() => setPressedBtn('editService')}
                  onMouseUp={() => setPressedBtn(null)}
                  onClick={() => setEditMode(true)}
                >
                  {t('EDIT_PROFILE')}
                </button>

                <button
                  style={modalSecondaryButtonStyle(
                    pressedBtn,
                    'closeService',
                    hoverBtn === 'closeService'
                  )}
                  onMouseEnter={() => setHoverBtn('closeService')}
                  onMouseLeave={() => {
                    setHoverBtn(null)
                    setPressedBtn(null)
                  }}
                  onMouseDown={() => setPressedBtn('closeService')}
                  onMouseUp={() => setPressedBtn(null)}
                  onClick={onClose}
                >
                  {t('CLOSE')}
                </button>
                {showServiceNavigation && (
<button
  style={{
    ...modalSecondaryButtonStyle(
      pressedBtn,
      'nextService',
      hoverBtn === 'nextService'
    ),
    width: 50,
    minWidth: 40,
    padding: 0,
    opacity: !hasNextService ? 0.4 : 1,
    cursor: !hasNextService ? 'not-allowed' : 'pointer'
  }}
  disabled={!hasNextService}
  onMouseEnter={() => setHoverBtn('nextService')}
  onMouseLeave={() => {
    setHoverBtn(null)
    setPressedBtn(null)
  }}
  onMouseDown={() => setPressedBtn('nextService')}
  onMouseUp={() => setPressedBtn(null)}
  onClick={() => onNextService?.()}
>
  <img
    src={arrowRight}
    alt="next"
    style={{
      width: 18,
      height: 18,
      pointerEvents: 'none',
      filter: 'invert(33%) sepia(24%) saturate(746%) hue-rotate(120deg) brightness(85%) contrast(90%)'
    }}
  />
</button>
                )}
              </>
            ) : (
              <>
                <button
                  style={modalPrimaryButtonStyle(
                    pressedBtn,
                    'saveService',
                    hoverBtn === 'saveService'
                  )}
                  onMouseEnter={() => setHoverBtn('saveService')}
                  onMouseLeave={() => {
                    setHoverBtn(null)
                    setPressedBtn(null)
                  }}
                  onMouseDown={() => setPressedBtn('saveService')}
                  onMouseUp={() => setPressedBtn(null)}
                  onClick={saveService}
                >
                  {t('SAVE')}
                </button>

                <button
                  style={modalSecondaryButtonStyle(
                    pressedBtn,
                    'cancelService',
                    hoverBtn === 'cancelService'
                  )}
                  onMouseEnter={() => setHoverBtn('cancelService')}
                  onMouseLeave={() => {
                    setHoverBtn(null)
                    setPressedBtn(null)
                  }}
                  onMouseDown={() => setPressedBtn('cancelService')}
                  onMouseUp={() => setPressedBtn(null)}
                  onClick={() => {
                    setForm(original)
                    setErrors({})
                    setEditMode(false)
                  }}
                >
                  {t('CANCEL')}
                </button>
              </>
            )}
          </div>

        </div>
      </div>
    </>
  )
}

export default ServiceDetailModal